import { accentSegment } from "@ui/lib/accent-classes";
import { tv, type VariantProps } from "@ui/lib/tv";

const topBarProgress = tv({
  slots: {
    root: "flex w-full gap-2",
    segment: "h-1 flex-1",
  },
  variants: {
    color: {
      red: {},
      green: {},
      purple: {},
      yellow: {},
    },
    active: {
      true: {},
      false: { segment: "bg-neutral-100" },
    },
  },
  compoundVariants: [
    { color: "red", active: true, class: { segment: accentSegment.red } },
    { color: "green", active: true, class: { segment: accentSegment.green } },
    { color: "purple", active: true, class: { segment: accentSegment.purple } },
    { color: "yellow", active: true, class: { segment: accentSegment.yellow } },
  ],
  defaultVariants: {
    color: "red",
  },
});

type TopBarProgressVariants = VariantProps<typeof topBarProgress>;

export interface TopBarProgressProps {
  steps: number;
  currentStep: number;
  color?: TopBarProgressVariants["color"];
  className?: string;
}

export function TopBarProgress({ steps, currentStep, color = "red", className }: TopBarProgressProps) {
  const styles = topBarProgress({ color });
  const activeSegment = topBarProgress({ color, active: true }).segment();
  const inactiveSegment = topBarProgress({ color, active: false }).segment();

  return (
    <div
      className={styles.root({ class: className })}
      role="progressbar"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={steps}
    >
      {Array.from({ length: steps }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: segments are static and won't reorder
        <div key={index} className={index < currentStep ? activeSegment : inactiveSegment} />
      ))}
    </div>
  );
}
